/**
 * Segment and line intersection atoms.
 *
 * Domain-neutral 2D geometry on plain `{ x, y }` points. This file has no
 * imports: no Victor, no Vue/Vuetify, no lodash, nothing from `src/features/`.
 * No function here mutates its inputs, and none of them throw for numeric
 * input: degenerate configurations are reported by returning `null`.
 *
 * @module lib/geom/segments
 */

/**
 * Absolute threshold applied to the 2D cross product of the two segment
 * directions (the denominator of the parametric solution) to detect
 * parallel, collinear and degenerate configurations.
 *
 * This is an **absolute** epsilon expressed in the same units as the input
 * coordinates. It is tuned for pixel-scale coordinates; callers working in
 * normalized coordinates (e.g. `[0, 1]` image space) have much smaller
 * cross products and may need to scale their inputs up — or apply their own
 * tolerance — before relying on the parallel test.
 *
 * @type {number}
 */
export const EPSILON = 1e-10;

/**
 * Intersects the two finite segments `a = (a0, a1)` and `b = (b0, b1)`.
 *
 * The parametric solution is `a0 + t * d1 == b0 + u * d2`, with
 * `d1 = a1 - a0`, `d2 = b1 - b0` and `denom = d1.x * d2.y - d1.y * d2.x`.
 * The returned point is always computed by substituting `t` back into
 * segment `a`: `{ x: a0.x + t * d1.x, y: a0.y + t * d1.y }`.
 *
 * Conventions and limitations:
 * - **Inclusive bounds**: `t` and `u` must lie in `[0, 1]`; the range test uses
 *   plain `<` / `>`, so an exact endpoint touch (`t` or `u` equal to `0` or `1`)
 *   counts as an intersection and the point is returned.
 * - **Collinear returns `null`**: a single `Math.abs(denom) < EPSILON` test
 *   covers both parallel-non-coincident and collinear-coincident inputs.
 *   Collinear overlaps are never reported, not even when the segments share an
 *   endpoint or overlap over a whole stretch — there is no single crossing
 *   point to return.
 * - **Degenerate segments return `null`**: a zero-length segment has no
 *   direction, so `a0 ≡ a1` or `b0 ≡ b1` yields `null`.
 * - The `EPSILON` threshold is the absolute `1e-10` documented on
 *   {@link EPSILON}; normalized-coordinate callers may need to adjust.
 *
 * @param {{x: number, y: number}} a0 start of segment `a`
 * @param {{x: number, y: number}} a1 end of segment `a`
 * @param {{x: number, y: number}} b0 start of segment `b`
 * @param {{x: number, y: number}} b1 end of segment `b`
 * @param {{x: number, y: number}} [out] optional target; when provided its `x`
 *   and `y` are overwritten and `out` itself is returned, so hot loops can
 *   reuse one scratch object instead of allocating per call
 * @returns {{x: number, y: number}|null} the crossing point (`out` when given,
 *   otherwise a fresh object), or `null` when the segments do not cross within
 *   `t, u ∈ [0, 1]`, are parallel/collinear, or either one is zero-length
 */
export function intersectSegments(a0, a1, b0, b1, out) {
    // Direction vectors d1 = a1 - a0 and d2 = b1 - b0, kept as scalars so the
    // function allocates nothing beyond the (optional) result object.
    const d1x = a1.x - a0.x;
    const d1y = a1.y - a0.y;
    const d2x = b1.x - b0.x;
    const d2y = b1.y - b0.y;

    // Zero-length guard: a degenerate segment has no direction to intersect
    // along. (It also forces denom to 0, but the explicit test keeps the
    // contract unambiguous.)
    if ((d1x === 0 && d1y === 0) || (d2x === 0 && d2y === 0)) return null;

    // Parallel / collinear guard.
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < EPSILON) return null;

    // w = b0 - a0
    const wx = b0.x - a0.x;
    const wy = b0.y - a0.y;

    const t = (wx * d2y - wy * d2x) / denom; // position along segment a
    const u = (wx * d1y - wy * d1x) / denom; // position along segment b

    // Inclusive [0, 1] on both parameters: exact endpoint touches are kept.
    if (t < 0 || t > 1 || u < 0 || u > 1) return null;

    const x = a0.x + t * d1x;
    const y = a0.y + t * d1y;

    if (out) {
        out.x = x;
        out.y = y;
        return out;
    }
    return { x, y };
}

/**
 * Intersects the two infinite lines through `(a0, a1)` and `(b0, b1)`.
 *
 * Same math as {@link intersectSegments} — `denom = d1.x * d2.y - d1.y * d2.x`,
 * `t = (w.x * d2.y - w.y * d2.x) / denom` with `w = b0 - a0`, and
 * `point = a0 + t * d1` — but **without** the `[0, 1]` clamp, so the result may
 * lie well outside either defining segment. Because the operations are the
 * same in form and order as the Victor-based
 * `mathUtils.calculateIntersectionPointBetweenLines`, the two agree
 * numerically on non-parallel input; only the Victor wrapper is gone.
 *
 * Conventions and limitations:
 * - **Parallel returns `null`** when `Math.abs(denom) < EPSILON`; collinear
 *   (coincident) lines fall under the same test and also return `null`, even
 *   though they share infinitely many points.
 * - **Degenerate segments return `null`**: `a0 ≡ a1` or `b0 ≡ b1` does not
 *   define a line direction.
 * - The threshold is the absolute `1e-10` documented on {@link EPSILON};
 *   normalized-coordinate callers may need to adjust.
 * - There is no `t ∈ [0, 1]` restriction here; that inclusive convention
 *   applies to {@link intersectSegments} only.
 *
 * @param {{x: number, y: number}} a0 first point defining line `a`
 * @param {{x: number, y: number}} a1 second point defining line `a`
 * @param {{x: number, y: number}} b0 first point defining line `b`
 * @param {{x: number, y: number}} b1 second point defining line `b`
 * @param {{x: number, y: number}} [out] optional target; when provided its `x`
 *   and `y` are overwritten and `out` itself is returned (no allocation)
 * @returns {{x: number, y: number}|null} the intersection point (`out` when
 *   given, otherwise a fresh object), or `null` when the lines are
 *   parallel/collinear or either defining segment is zero-length
 */
export function intersectLines(a0, a1, b0, b1, out) {
    const d1x = a1.x - a0.x;
    const d1y = a1.y - a0.y;
    const d2x = b1.x - b0.x;
    const d2y = b1.y - b0.y;

    if ((d1x === 0 && d1y === 0) || (d2x === 0 && d2y === 0)) return null;

    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < EPSILON) return null;

    // w = b0 - a0
    const wx = b0.x - a0.x;
    const wy = b0.y - a0.y;

    const t = (wx * d2y - wy * d2x) / denom;

    const x = a0.x + t * d1x;
    const y = a0.y + t * d1y;

    if (out) {
        out.x = x;
        out.y = y;
        return out;
    }
    return { x, y };
}
